import React, { useState } from 'react';
import { useRoll } from '@root/hooks';
import { exitToLobby } from '@root/lib';
import { PAWN_COLOURS, type PawnColour } from '@root/lib/pawnColour';
import { balanceSelector } from '@root/store/slices/balanceSlice';
import { betValuesSelector } from '@root/store/slices/betValuesSlice';
import { journeySelector } from '@root/store/slices/journeySlice';
import { useActions, useTypedSelector } from '@root/store/store';
import { useImageUrl } from '@shared/components/ImageContext';
import { useBetLimits } from '@shared/hooks';
import { formatMoney } from '@shared/lib';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import styles from './StartScreen.module.scss';

// Three presets beside the free-text amount.
const QUICK_AMOUNTS = [100, 500, 1000];

// ₹1,000 shows as ₹1K on the preset buttons.
function shortAmount(symbol: string, value: number): string {
  return value >= 1000 ? `${symbol}${value / 1000}K` : `${symbol}${value}`;
}

// Design shows whole currency, no decimals.
function wholeAmount(symbol: string, value: number): string {
  return `${symbol}${Math.round(value).toLocaleString('en-IN')}`;
}

// The stake locks for the run, so this is the only place to set it.
const StartScreen: React.FC = () => {
  const { t } = useTranslation();
  const { colour } = useTypedSelector(journeySelector);
  const { currentBetAmount } = useTypedSelector(betValuesSelector);
  const { balance } = useTypedSelector(balanceSelector);
  const { setPawnColour, setCurrentBetAmount, setGameRulesModalOpen } =
    useActions();
  const { roll, canRoll, hasRun } = useRoll();
  const { img, imgProps } = useImageUrl();
  const [dismissed, setDismissed] = useState(false);

  // Per-currency, from operator config, with a fallback when it has no row.
  const { minBet, maxBet } = useBetLimits();

  if (hasRun || dismissed) return null;

  const symbol = balance.currencySymbol;
  const belowMin = currentBetAmount < minBet;
  const aboveMax = currentBetAmount > maxBet;
  const outOfRange = belowMin || aboveMax;
  const shortfall = Math.max(0, currentBetAmount - balance.amount);
  const canStart = !outOfRange && shortfall === 0;

  const helper = t('{{min}}–{{max}} per paid roll.', {
    min: wholeAmount(symbol, minBet),
    max: wholeAmount(symbol, maxBet),
  });

  return (
    <div className={styles.scrim}>
      <div
        className={styles.dialog}
        style={
          { '--panel-skin': `url(${img('PopupFrame')})` } as React.CSSProperties
        }
      >
        <div className={styles.head}>
          <h2 className={styles.title}>{t('Ready for the last mile?')}</h2>
          <button
            type="button"
            className={styles.close}
            aria-label={t('Close')}
            onClick={() => setDismissed(true)}
          >
            <img {...imgProps('Icon_Close')} alt="" />
          </button>
        </div>

        <p className={styles.label}>{t('Choose your colour')}</p>
        <div className={styles.colours}>
          {PAWN_COLOURS.map((c: PawnColour) => (
            <button
              type="button"
              key={c}
              aria-label={t(c)}
              aria-pressed={c === colour}
              className={cn(styles.colour, {
                [styles.colour_active]: c === colour,
              })}
              onClick={() => setPawnColour(c)}
            >
              <img {...imgProps(`Pawn_${c}`)} alt="" className={styles.pawn} />
              <p className={styles.colourName}>
                {c === colour ? `✓ ${t(c)}` : t(c)}
              </p>
            </button>
          ))}
        </div>

        <p className={styles.sublabel}>{t('Amount per paid roll')}</p>
        <div
          className={cn(styles.amountField, {
            [styles.amountField_invalid]: outOfRange,
          })}
        >
          <span className={styles.rupee}>{symbol}</span>
          <input
            type="number"
            inputMode="decimal"
            className={styles.amountInput}
            // Sized to its digits so it stays beside the ₹.
            style={{ width: `${String(currentBetAmount).length + 1}ch` }}
            value={currentBetAmount}
            min={minBet}
            max={maxBet}
            onChange={(e) => setCurrentBetAmount(Number(e.target.value))}
          />
        </div>
        <p
          className={cn(styles.helper, { [styles.helper_invalid]: outOfRange })}
        >
          {helper}
        </p>

        <div className={styles.quick}>
          {QUICK_AMOUNTS.map((value) => (
            <button
              type="button"
              key={value}
              className={cn(styles.quickButton, {
                [styles.quickButton_active]: value === currentBetAmount,
              })}
              style={
                {
                  '--button-skin': `url(${img('ButtonSkin_Secondary')})`,
                } as React.CSSProperties
              }
              onClick={() => setCurrentBetAmount(value)}
            >
              {shortAmount(symbol, value)}
            </button>
          ))}
        </div>

        <p className={styles.balance}>
          {t('Available balance: {{amount}}', {
            amount: formatMoney(symbol, balance.amount),
          })}
        </p>
        {shortfall > 0 && !outOfRange && (
          <p className={styles.shortfall}>
            {t('Add {{short}} to start at {{amount}} per roll.', {
              short: wholeAmount(symbol, shortfall),
              amount: wholeAmount(symbol, currentBetAmount),
            })}
          </p>
        )}

        <p className={styles.copy}>
          {t(
            'Each paid roll costs your selected amount. Total round cost varies. After 10 seconds, a ready turn rolls automatically—even while you are away.'
          )}
        </p>
        <p className={styles.copyMuted}>
          {t(
            'No balance for a paid roll? Resume within 60 minutes or the round ends as a loss.'
          )}
        </p>

        <button
          type="button"
          className={cn(styles.skin, styles.skin_primary)}
          style={
            {
              '--button-skin': `url(${img('ButtonSkin_Primary')})`,
            } as React.CSSProperties
          }
          disabled={shortfall === 0 && (!canRoll || !canStart)}
          onClick={() => (shortfall > 0 ? exitToLobby() : void roll())}
        >
          {shortfall > 0 ? t('ADD BALANCE') : t('START ROUND')}
        </button>
        <button
          type="button"
          className={cn(styles.skin, styles.skin_secondary)}
          style={
            {
              '--button-skin': `url(${img('ButtonSkin_Secondary')})`,
            } as React.CSSProperties
          }
          onClick={() => setGameRulesModalOpen(true)}
        >
          {t('How to play')}
        </button>
      </div>
    </div>
  );
};

export default StartScreen;
